import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';
import { BrandLogo, Button } from '@/ui';
import { ThemeToggle } from '@/app/ThemeToggle';
import { AnnouncementsButton } from '@/features/announcements/AnnouncementsButton';
import { useWinNotice } from '@/features/offers/win-notice';
import { env } from '@/env';
import { initialsOf } from '@/lib/initials';
import { useSessionStore } from '@/auth/session.store';
import { SidebarNav } from './SidebarNav';
import type { MenuItem } from './menus';
import styles from './AppShell.module.css';

/**
 * TEK KABUK.
 *
 * Uc bolge (musteri / buro / yonetim) icin uc ayri kabuk dosyasi YAZILMADI:
 * aralarindaki tek gercek fark menu verisi. Uc kopya, birinde duzeltilen bir
 * odak/erisilebilirlik hatasinin digerlerinde yasamaya devam etmesi demekti.
 *
 * `menu` verilmediginde kabuk KAPI EKRANLARI kipine gecer (giris, ilk giris,
 * sozlesmeler): kenar cubugu ve menu dugmesi yok, icerik ortalanir. Bu da ayni
 * dosyada, cunku ust cubuk ve marka o ekranlarda da aynidir.
 */

const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Hayalet tipografi (mobil: GhostType). Icerigin arkasinda %4 opaklikta dev bir
 * kelime; sag kenardan bilerek tasar. Bolge basina sabit — ceviri DEGIL, grafik
 * oge; bu yuzden buyuk harfle burada yazilir (BUYUK HARF YASAGI ihlal edilmez,
 * text-transform kullanilmiyor).
 */
const GHOST_WORDS: Record<string, string> = {
  'nav.zoneCustomer': 'KUR',
  'nav.zoneExchange': 'PİYASA',
  'nav.zoneAdmin': 'YÖNETİM',
};

export type AppShellProps = {
  /** Bolge menusu. Verilmezse kenar cubugu hic cizilmez. */
  menu?: readonly MenuItem[];
  /** Bolge etiketinin CEVIRI ANAHTARI. */
  zoneTitleKey?: string;
};

export function AppShell({ menu, zoneTitleKey }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const signOut = useSessionStore((state) => state.signOut);

  const hasSidebar = Boolean(menu && menu.length > 0);
  // Kazanma bildirimi (2026-10-02, K4): yalniz buro oturumunda ve bolge kabugunda (kapi
  // ekranlarinda degil). Musteri oturumunda hook hicbir istek atmaz.
  useWinNotice(hasSidebar);
  // Ust cubukta cizilecek oge (profil). Kapi ekranlarinda menu yok -> undefined.
  const profileItem = menu?.find((item) => item.placement === 'topbar');
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  /**
   * CEKMECE DURUMU BIR YOL TUTAR, boolean DEGIL.
   *
   * "Rota degisince kapan" kurali boylece ETKISIZ saglanir: cekmece yalniz
   * ACILDIGI yolda acik sayilir, gezinme onu kendiliginden kapatir. Bir
   * useEffect icinde setState cagirmak ayni sonucu fazladan bir render turuyla
   * ve geri/ileri tuslarinda bir kare gecikmeyle verirdi.
   */
  const [openAtPath, setOpenAtPath] = useState<string | null>(null);
  const drawerOpen = openAtPath === location.pathname;

  const closeDrawer = useCallback(() => {
    setOpenAtPath(null);
  }, []);

  // Genis ekrana gecildiginde durum sifirlanir. Yerlesim kararini CSS veriyor
  // (medya sorgusu); JS'te ekran genisligi olculup ikinci bir karar uretilseydi
  // ikisi ayrisir ve gorunmez bir perde ekrani kilitlerdi. Burada yalniz DIS
  // OLAYA abone olunuyor — ilk deger zaten "kapali".
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (): void => {
      if (mql.matches) setOpenAtPath(null);
    };
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  // Escape + ODAK TUZAGI. Acik bir off-canvas panelde Tab ile arkadaki icerige
  // kacilabiliyorsa panel ekran okuyucu ve klavye kullanicisi icin bir tuzaktan
  // ibarettir: gorunmeyen baglantilar arasinda dolasilir.
  useEffect(() => {
    if (!drawerOpen) return;
    const panel = drawerRef.current;
    panel?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeDrawer();
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [drawerOpen, closeDrawer]);

  const onSignOut = (): void => {
    void signOut().then(() => {
      void navigate('/giris', { replace: true });
    });
  };

  return (
    <div className={[styles.shell, hasSidebar ? styles.withSidebar : styles.bare].join(' ')}>
      <a className={styles.skipLink} href="#main">
        {t('nav.skipToContent')}
      </a>

      {zoneTitleKey && GHOST_WORDS[zoneTitleKey] ? (
        <span className={styles.ghost} aria-hidden="true">
          {GHOST_WORDS[zoneTitleKey]}
        </span>
      ) : null}

      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          {hasSidebar ? (
            <button
              ref={menuButtonRef}
              type="button"
              className={styles.menuButton}
              aria-expanded={drawerOpen}
              aria-controls="app-sidebar"
              aria-label={t('nav.openMenu')}
              onClick={() => {
                setOpenAtPath((current) => (current === null ? location.pathname : null));
              }}
            >
              {drawerOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
            </button>
          ) : null}

          <div className={styles.brand}>
            <BrandLogo variant="mark" className={styles.logoMark} />
            <span className={styles.mark}>
              <span className={styles.markInk}>KUR</span>
              <span className={styles.markGold}>-AL</span>
            </span>
            {zoneTitleKey ? <span className={styles.zone}>{t(zoneTitleKey)}</span> : null}
          </div>

          <div className={styles.topbarActions}>
            {/* DUYURULAR: bolgeden bagimsiz (musteri de buro da gorur), o yuzden menude
                degil ust cubukta. Oturum yoksa cizilmez — giris ekraninda anlamsiz olur
                ve uc zaten kimlik ister. */}
            {snapshot ? <AnnouncementsButton /> : null}
            <ThemeToggle />

            {/* PROFIL SAG UST KOSEDE (kenar cubugunda degil). Hedef yol menu
                verisinden geliyor — bolgeye gore degistigi icin ('/musteri/profil'
                vs '/buro/profil') burada elle yazilsaydi ucuncu bir bolge
                eklendiginde sessizce yanlis yere goturur. */}
            {profileItem && snapshot ? (
              <NavLink
                to={profileItem.to}
                className={({ isActive }) =>
                  [styles.profileLink, isActive ? styles.profileLinkActive : undefined]
                    .filter(Boolean)
                    .join(' ')
                }
              >
                <span className={styles.profileAvatar} aria-hidden="true">
                  {initialsOf(snapshot.user.userFullName ?? '')}
                </span>
                {/* "Profil" HER ZAMAN erisilebilir adin icinde: ad dar ekranda
                    gorsel olarak dusuyor (CSS) ve geriye yalniz bas harfler
                    kaliyor — o hal icin baglantinin nereye gittigini soyleyen
                    tek sey bu. Global `.visually-hidden` kullaniliyor (bkz.
                    design/global.css); modul icinde ikinci bir kopya yok. */}
                <span className="visually-hidden">{t('nav.profile')}</span>
                <span className={styles.profileName}>
                  {snapshot.user.userFullName ?? ''}
                </span>
              </NavLink>
            ) : null}

            {snapshot ? (
              <Button
                tone="ghost"
                size="sm"
                iconLeft={<LogOut size={16} aria-hidden="true" />}
                onClick={onSignOut}
                // Dar ekranda GORSEL metin duser (asagidaki .actionLabel), geriye
                // yalniz ikon kalir. `aria-label` HER ZAMAN duruyor ki o halde de
                // dugmenin adi kaybolmasin; metinle ayni oldugu icin "gorunen ad
                // erisilebilir adin icinde olmali" kuralini da bozmuyor.
                aria-label={t('actions.logout')}
              >
                <span className={styles.actionLabel}>{t('actions.logout')}</span>
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {hasSidebar && menu ? (
          <>
            {/* Perde yalniz cekmece acikken; genis ekranda CSS ile gizli. */}
            {drawerOpen ? (
              <div
                className={styles.scrim}
                role="presentation"
                onMouseDown={closeDrawer}
              />
            ) : null}
            <div
              id="app-sidebar"
              ref={drawerRef}
              tabIndex={-1}
              className={[styles.sidebar, drawerOpen ? styles.sidebarOpen : undefined]
                .filter(Boolean)
                .join(' ')}
            >
              {snapshot?.organization?.name ? (
                <p className={styles.orgName}>{snapshot.organization.name}</p>
              ) : null}
              <SidebarNav items={menu} snapshot={snapshot} onNavigate={closeDrawer} />
            </div>
          </>
        ) : null}

        <main id="main" className={styles.main}>
          <Outlet />
        </main>
      </div>

      <footer className={styles.footer}>
        <span className={styles.version}>
          {t('profile.version')} {env.VITE_APP_VERSION}
        </span>
      </footer>
    </div>
  );
}
